import { enumTransportationAggregateType } from '@rematter/frontend-lib-api-client';
import { Col, Row, Loading, cn } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideBox,
  LucideClipboardList,
  LucideContainer,
  LucidePlane,
  LucideRoute,
  LucideShip,
  LucideTrainFront,
  LucideTruck,
  LucideUser,
} from 'lucide-react';

import {
  $remoteTransportationOutboxEventCounts,
  useTransportationAggregateTypeFilter,
} from '@/audit-log/_state/transportationEvents.state';
import { useTranslations } from '@/audit-log/i18n/useTranslations';

const aggregateTypeConfig = [
  {
    type: enumTransportationAggregateType.Asset,
    labelKey: 'asset' as const,
    icon: LucideBox,
    countKey: 'asset' as const,
  },
  {
    type: enumTransportationAggregateType.Driver,
    labelKey: 'driver' as const,
    icon: LucideUser,
    countKey: 'driver' as const,
  },
  {
    type: enumTransportationAggregateType.Truck,
    labelKey: 'truck' as const,
    icon: LucideTruck,
    countKey: 'truck' as const,
  },
  {
    type: enumTransportationAggregateType.Trailer,
    labelKey: 'trailer' as const,
    icon: LucideContainer,
    countKey: 'trailer' as const,
  },
  {
    type: enumTransportationAggregateType.Route,
    labelKey: 'route' as const,
    icon: LucideRoute,
    countKey: 'route' as const,
  },
  {
    type: enumTransportationAggregateType.Service,
    labelKey: 'service' as const,
    icon: LucideClipboardList,
    countKey: 'service' as const,
  },
  {
    type: enumTransportationAggregateType.ContainerType,
    labelKey: 'containerType' as const,
    icon: LucideContainer,
    countKey: 'containerType' as const,
  },
  {
    type: enumTransportationAggregateType.TransportationType,
    labelKey: 'transportationType' as const,
    icon: LucideTruck,
    countKey: 'transportationType' as const,
  },
  {
    type: enumTransportationAggregateType.FreightForwarder,
    labelKey: 'freightForwarder' as const,
    icon: LucidePlane,
    countKey: 'freightForwarder' as const,
  },
  {
    type: enumTransportationAggregateType.FreightLine,
    labelKey: 'freightLine' as const,
    icon: LucideTrainFront,
    countKey: 'freightLine' as const,
  },
  {
    type: enumTransportationAggregateType.ShippingCarrier,
    labelKey: 'shippingCarrier' as const,
    icon: LucideShip,
    countKey: 'shippingCarrier' as const,
  },
];

export const TransportationEventsSidebar = () => {
  const t = useTranslations();
  const [selectedType, setSelectedType] =
    useTransportationAggregateTypeFilter();
  const { data: counts, isLoading } = useAtomValue(
    $remoteTransportationOutboxEventCounts,
  );

  const totalCount = counts
    ? counts.asset +
      counts.driver +
      counts.truck +
      counts.trailer +
      counts.route +
      counts.service +
      counts.containerType +
      counts.transportationType +
      counts.freightForwarder +
      counts.freightLine +
      counts.shippingCarrier
    : 0;

  return (
    <Col className="@TransportationEventsSidebar w-[200px] border-r border-neutral/10 p-3">
      <Loading isLoading={isLoading}>
        <Col gap2>
          <button
            onClick={() => setSelectedType(undefined)}
            className={cn(
              'flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors',
              selectedType === undefined
                ? 'bg-primary/10 text-primary'
                : 'hover:bg-neutral/5',
            )}
          >
            <span>{t.transportationEventsView.sidebar.all}</span>
            <span className="text-xs text-neutral/50">{totalCount}</span>
          </button>

          {aggregateTypeConfig.map(
            ({ type, labelKey, icon: Icon, countKey }) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={cn(
                  'flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors',
                  selectedType === type
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-neutral/5',
                )}
              >
                <Row gap2 yCenter>
                  <Icon size={16} className="text-neutral/60" />
                  <span>
                    {
                      t.transportationEventsView.sidebar.aggregateTypes[
                        labelKey
                      ]
                    }
                  </span>
                </Row>
                <span className="text-xs text-neutral/50">
                  {counts?.[countKey] ?? 0}
                </span>
              </button>
            ),
          )}
        </Col>
      </Loading>
    </Col>
  );
};
